import type { ControlProps } from "@jsonforms/core";
import { optionIs, rankWith } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import {
  File as FileIconGeneric,
  FileText,
  Image as ImageIcon,
  Video as VideoIcon,
  X,
} from "lucide-react";
import React from "react";

interface SlackFile {
  url: string;
  title?: string;
}

const SlackFilesControlComponent: React.FC<ControlProps> = ({
  data,
  enabled,
  handleChange,
  path,
  label,
  visible,
}) => {
  const files: SlackFile[] = Array.isArray(data) ? data : [];

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    handleChange(path, newFiles);
  };

  const getFileExtension = (url: string) => {
    return url.split(".").pop()?.toUpperCase() || "FILE";
  };

  const getDisplayName = (file: SlackFile) => {
    const name = file.title || file.url.split("/").pop() || "file";
    const baseName = name.includes(".")
      ? name.substring(0, name.lastIndexOf("."))
      : name;
    return baseName.length > 15 ? baseName.slice(0, 15) + "…" : baseName;
  };

  const getFileIcon = (url: string) => {
    const ext = url.split(".").pop()?.toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext || "")) {
      return <ImageIcon className="w-5 h-5 text-spectrum-mulberry" />;
    }
    if (["mp4", "mov", "avi"].includes(ext || "")) {
      return <VideoIcon className="w-5 h-5 text-spectrum-mulberry" />;
    }
    if (["pdf", "doc", "docx"].includes(ext || "")) {
      return <FileText className="w-5 h-5 text-spectrum-mulberry" />;
    }
    return <FileIconGeneric className="w-5 h-5 text-spectrum-mulberry" />;
  };

  const isImageFile = (url: string) => {
    const ext = url.split(".").pop()?.toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext || "");
  };

  const handleFileClick = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!visible) return null;
  if (files.length === 0) return null;

  return (
    <div className="mb-4 @container">
      <label className="text-sm font-medium mb-2 block">
        {label || "Attachments"}
      </label>
      <div className="grid grid-cols-1 @sm:grid-cols-2 gap-4">
        {files.map((file, index) => (
          <div
            key={index}
            className="relative inline-flex items-center gap-2 p-2 pr-4 rounded-2xl border transition-colors hover:bg-neutral-200 dark:hover:bg-neutral-800 max-w-full min-w-0 cursor-pointer"
            onClick={() => handleFileClick(file.url)}
          >
            {/* File icon/image */}
            <div className="relative flex items-center justify-center w-10 h-10 shrink-0 rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-700">
              {isImageFile(file.url) ? (
                <img
                  src={file.url}
                  alt={file.title || "file"}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // If image fails to load, show fallback icon
                    const parent = e.currentTarget.parentElement;
                    if (parent) {
                      e.currentTarget.remove();
                      const icon = document.createElement("div");
                      icon.innerHTML =
                        '<svg class="w-5 h-5 text-spectrum-mulberry" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>';
                      parent.appendChild(icon);
                    }
                  }}
                />
              ) : (
                getFileIcon(file.url)
              )}
            </div>

            {/* File info */}
            <div className="flex flex-col min-w-0 flex-1 text-sm">
              <span className="font-medium text-foreground truncate">
                {getDisplayName(file)}
              </span>
              <span className="text-xs text-muted-foreground">
                {getFileExtension(file.url)}
              </span>
            </div>

            {/* Remove button */}
            {enabled && (
              <button
                className="ml-auto inline-flex items-center justify-center w-6 h-6 rounded-full bg-neutral-300/60 dark:bg-neutral-700/60 hover:bg-neutral-300 dark:hover:bg-neutral-700 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation(); // Prevent file click when removing
                  removeFile(index);
                }}
                aria-label="Remove file"
              >
                <X className="w-3.5 h-3.5 text-neutral-700 dark:text-neutral-300" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const slackFilesTester = rankWith(5, optionIs("control", "slack_files"));

export const SlackFilesControl = withJsonFormsControlProps(
  SlackFilesControlComponent,
);
